import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import log from 'loglevel';
import authService, { privileges } from '@/shared/services/AuthService';
import moment from 'moment';
import { injectIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';

import api from '@/shared/api';
import ToastrService from '@/shared/services/ToastrService';
import ScrollableTable from '@/shared/components/table/ScrollableTable';
import ColumnHeader from '@/shared/components/table/ColumnHeader';
import SimplePanel from '@/shared/components/SimplePanel';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import SignalRService from '@/shared/services/SignalRService';
import withDropZone from '@/shared/components/dragAndDrop/withDropzone';
import { injectStateService } from '@/shared/services/StateService';
import handleHttpError from '@/shared/services/HandleHttpError';
import Checkbox from '@/shared/components/input/Checkbox';
import YesNoModal from '@/shared/components/YesNoModal';
import userStorage from '@/shared/services/userStorage';
import DataCacheService from '@/shared/services/DataCacheService';
import ShareItemModal from '@/shared/components/ShareItemModal';
import ForwardEmailModal from '@/shared/components/email/ForwardEmailModal';
import ReplyToEmailModal from '@/shared/components/email/ReplyToEmailModal';
import { getRelationName } from '@/shared/components/RelationName';
import SetIsPublicOnPortalModal from './modals/SetIsPublicOnPortalModal';
import ChangeDocumentDateModal from './modals/ChangeDocumentDateModal';
import messages from './documentMessages';
import EmailRow from './EmailRow';
import Dropzone from './Dropzone';
import DocumentDetailsModal from './details/DocumentDetailsModal';
import { containsDragFormat } from '../../shared/utils/dragEventHelpers';
import EmailFilters, { defaultFilters } from './EmailFilters';
import EmailGlobalActionsButton from './EmailGlobalActionsButton';
import UpdateDocumentTagsModal from './modals/UpdateDocumentTagsModal';
import UpdateDocumentRelationsModal from './modals/UpdateDocumentRelationsModal';
import CreateToDoModal from '../tasks/CreateToDoModal';
import config from '../../config';

class EmailsContainer extends Component {
  state = {
    emails: [],
    showDocumentDetailsModal: false,
    initialSearchParams: {},
    allSelected: false,
    showUpdateTagsModal: false,
    showUpdateRelationsModal: false,
    showRemoveBatchDocumentsModal: false,
    showCreateTodoModal: false,
    showSetPublicOnPortalModal: false,
    showChangeDocumentDateModal: false,
  };

  constructor(props) {
    super(props);
    const {
      searchParams: { mainFilters: searchParamsFilters = {}, ...otherSearchParams } = {},
    } = props;

    let mainFilters = { ...defaultFilters };
    if (searchParamsFilters && Object.keys(searchParamsFilters).length) {
      mainFilters = { ...mainFilters, ...searchParamsFilters };
    } else {
      const storageFilters = userStorage.getItem('EmailsOverviewFilters') || {};

      if (storageFilters.dateFrom) {
        storageFilters.dateFrom = moment.utc(storageFilters.dateFrom);
      }
      if (storageFilters.dateUntil) {
        storageFilters.dateUntil = moment.utc(storageFilters.dateUntil);
      }
      if (storageFilters.documentDateFrom) {
        storageFilters.documentDateFrom = moment.utc(storageFilters.documentDateFrom);
      }
      if (storageFilters.documentDateUntil) {
        storageFilters.documentDateUntil = moment.utc(storageFilters.documentDateUntil);
      }
      mainFilters = { ...mainFilters, ...storageFilters };
    }

    this.state = {
      ...this.state,
      initialSearchParams: {
        sortKey: 'creationDate',
        desc: true,
        pageSize: 50,
        mainFilters,
        ...otherSearchParams,
      },
    };
  }

  static propTypes = {
    intl: intlShape.isRequired,
    searchParams: PropTypes.shape({}),
  };

  static defaultProps = {
    searchParams: {},
  };

  documentDeleteHandler = documentId => {
    this.setState(({ emails }) => ({
      emails: emails.filter(email => email.documentId !== documentId),
    }));
  };
  emailCreatedHandler = email => {
    this.setState(state => {
      const { emails } = state;

      const index = emails.findIndex(x => x.documentId === email.documentId);
      if (index !== -1) {
        const copy = [...emails];

        copy[index] = {
          ...copy[index],
          ...email,
        };
        return {
          emails: copy,
        };
      }
      return {
        emails: [email, ...emails],
      };
    });
  };

  componentDidMount() {
    SignalRService.subscribe('documentDeleted', this.documentDeleteHandler);
    SignalRService.subscribe('emailCreated', this.emailCreatedHandler);
  }

  componentWillUnmount() {
    SignalRService.unsubscribe('documentDeleted', this.documentDeleteHandler);
    SignalRService.unsubscribe('emailCreated', this.emailCreatedHandler);

    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to leaving page.');
    }
  }

  onDocumentUploaded = error => {
    const { intl } = this.props;
    if (error) {
      log.error(error);

      if (error.body) {
        const translatedError = {
          type: 'documentdomainexceptioncode',
          code: error.body,
        };

        handleHttpError(intl)(translatedError);
      } else if (error.code) {
        handleHttpError(intl)(error);
      }
    } else {
      ToastrService.success(intl.formatMessage(messages.DocumentWasUploaded));
    }
  };

  onUploadDocument = ({ files }) => {
    const canCreate = authService.hasPermission({
      functionality: 'documents',
      privilege: privileges.Create,
    });
    if (!canCreate) return;

    const { pond } = this;
    if (!pond) return;

    const filePondFiles = files.map(x => ({
      source: x,
    }));

    pond.addFiles(filePondFiles);
  };

  deleteDocument(id) {
    const { emails } = this.state;
    const { intl } = this.props;

    return api.base.delete(`${config.appUrls.documents}/api/documents/${id}`).then(
      () => {
        ToastrService.success(intl.formatMessage(messages.MsgDeleteDocumentSuccess));
        this.setState(state => {
          const closeDetailModal =
            state.showDocumentDetailsModal && state.activeEmail.documentId === id;

          return {
            emails: emails.filter(document => document.documentId !== id),
            activeEmail: closeDetailModal ? null : state.activeEmail,
            showDocumentDetailsModal: closeDetailModal ? false : state.showDocumentDetailsModal,
            showSingleDeleteDocumentModal: false,
          };
        });
      },
      error => {
        console.error(error);
        if (error.response && error.response.data && error.response.data.code) {
          handleHttpError(intl)(error);
        } else {
          ToastrService.error(intl.formatMessage(messages.MsgDeleteDocumentError));
        }
        log.error('error deleting document ', error);
      },
    );
  }

  filtersChanged(filters) {
    this.setState(state => ({
      ...state,
      initialSearchParams: {
        ...state.initialSearchParams,
        page: 0,
        mainFilters: { ...state.initialSearchParams.mainFilters, ...filters },
      },
    }));
  }

  loadEmails = (
    { tags, desc, sortKey: sort, page, pageSize: take, mainFilters = {} },
    requestNr,
  ) => {
    if (this.cancelToken && !this.cancelToken.token.reason) {
      this.cancelToken.cancel('Operation canceled due to new request.');
    }
    this.cancelToken = Axios.CancelToken.source();
    this.lastRequestNr = requestNr;

    const { relationId = null, ...rest } = mainFilters;

    const params = {
      desc,
      sort,
      page,
      take,
      ...rest,
    };

    if (relationId) {
      params.linkId = relationId;
      params.linkType = 3; // link type 3 = relation
    }

    const storageFilters = { ...mainFilters };
    userStorage.setItem('EmailsOverviewFilters', storageFilters);

    return api.base
      .post(`${config.appUrls.documents}/api/emails/GetEmails`, params, {
        cancelToken: this.cancelToken.token,
      })
      .then(({ data }) => {
        if (this.lastRequestNr !== requestNr) return;
        const { allSelected } = this.state;
        const newEmails = data.emails.map(e => ({ ...e, selected: !!allSelected }));

        this.setState(state => ({
          ...state,
          emails: page > 0 ? [...state.emails, ...newEmails] : [...newEmails],
          currentSearchParams: {
            mainFilters,
            ...params,
          },
        }));

        return data.emails;
      })
      .catch(error => {
        if (!Axios.isCancel(error)) {
          if (page <= 0 && this.lastRequestNr === requestNr) {
            this.setState({ emails: [] });
          }
          return error;
        }
        // Return empty array for cancelled requests so ScrollableTable can handle it properly
        return [];
      });
  };

  getPreviousDocumentId = documentId => {
    const { emails } = this.state;

    if (!emails) return;

    const currentIndex = emails.map(x => x.documentId).findIndex(x => x === documentId);

    // No previous doc present
    if (currentIndex <= 0) return;

    const previousIndex = currentIndex - 1;
    if (previousIndex < emails.length) {
      return emails[previousIndex].documentId;
    }
  };

  getNextDocument = documentId => {
    const { emails } = this.state;
    if (!emails) return null;
    const currentIndex = emails.map(x => x.documentId).findIndex(x => x === documentId);
    const nextIndex = currentIndex + 1;
    if (nextIndex < emails.length) {
      return emails[nextIndex];
    }
    return null;
  };
  getNextDocumentId = documentId =>
    new Promise((resolve, reject) => {
      const nextDocument = this.getNextDocument(documentId);
      if (nextDocument) {
        return resolve(nextDocument.documentId);
      }

      // Need to retrieve next page
      const { currentSearchParams } = this.state;

      const params = {
        ...currentSearchParams,
        page: currentSearchParams.page + 1,
      };

      api.base.post(`${config.appUrls.documents}/api/emails/GetEmails`, params).then(({ data }) => {
        const { emails: newEmails } = data;
        if (!newEmails || newEmails.length === 0) {
          return reject();
        }

        this.setState(
          ({ emails }) => ({
            emails: params.page > 0 ? [...emails, ...newEmails] : [...data],
            currentSearchParams: params,
          }),
          () => {
            const nextDocument2 = this.getNextDocument(documentId);
            if (nextDocument2) {
              resolve(nextDocument2.documentId);
            } else {
              reject();
            }
          },
        );
      }, reject);
    });

  handleSelection = email => {
    this.setState(({ emails }) => ({
      emails: emails.map(x => (x.id === email.id ? { ...x, selected: !x.selected } : x)),
    }));
  };

  renderRow = email => {
    const { intl } = this.props;
    return (
      <EmailRow
        key={email.id}
        email={email}
        intl={intl}
        onResend={activeEmail => this.setState({ showResendConfirmation: true, activeEmail })}
        onOpenDocumentDetails={activeEmail =>
          this.setState({ showDocumentDetailsModal: true, activeEmail })
        }
        handleSelection={this.handleSelection}
        onDeleteDocument={activeEmail =>
          this.setState({ showSingleDeleteDocumentModal: true, activeEmail })
        }
        onCreateTodo={activeEmail => this.setState({ showCreateTodoModal: true, activeEmail })}
        onShareItem={activeEmail => this.setState({ showShareItemModal: true, activeEmail })}
        onDeleteRelation={relationId =>
          this.setState({
            showDeleteRelationLinkModal: true,
            activeEmail: email,
            activeRelationId: relationId,
          })
        }
        onForward={activeEmail => this.setState({ showForwardModal: true, activeEmail })}
        onReplyTo={(activeEmail, replyAll) =>
          this.setState({ showReplyToModal: true, activeEmail, replyAll })
        }
      />
    );
  };

  handleListDragOver = e => {
    if (!e.dataTransfer) return;
    if (containsDragFormat(e, 'Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };
  handleListDrop = e => {
    if (!e.dataTransfer) return;
    try {
      if (containsDragFormat(e, 'Files')) {
        e.preventDefault();
        this.onUploadDocument({ files: [...e.dataTransfer.files] });
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  onDetailModalClosed = updatedDocument => {
    if (!updatedDocument) return;

    this.setState(state => {
      const { emails } = state;

      const index = emails.findIndex(x => x.documentId === updatedDocument.id);
      if (index !== -1) {
        const copy = [...emails];

        copy[index] = {
          ...copy[index],
          relations: updatedDocument.relations,
        };
        return {
          emails: copy,
        };
      }
      return {
        emails: [updatedDocument, ...emails],
      };
    });
  };

  getSelectionCountFormatted() {
    const { allSelected, emails } = this.state;
    const { intl } = this.props;
    if (allSelected) {
      return intl.formatMessage(messages.AllEmailsSelected);
    }

    const count = emails ? emails.filter(x => x.selected === true).length : 0;
    return intl.formatMessage(messages.CountEmailsSelected, { count });
  }

  getSelectionCount() {
    const { allSelected, totalRows, emails } = this.state;

    const count = emails ? emails.filter(x => x.selected !== allSelected).length : 0;

    return allSelected ? totalRows - count : count;
  }

  handleSelectAll = () => {
    const { emails, allSelected } = this.state;

    const isCurrentlySelected = !allSelected;

    const emailsCopy = emails.map(e => ({ ...e, selected: !!isCurrentlySelected }));

    this.setState(state => ({
      ...state,
      allSelected: isCurrentlySelected,
      emails: emailsCopy,
    }));
  };

  isGlobalActionsDisabled = () => {
    const { emails, allSelected } = this.state;
    if (!emails || !emails.length) return true;
    if (emails.some(x => x.selected) || allSelected) return false;

    return true;
  };

  getSelectionParams = () => {
    const { emails, allSelected } = this.state;

    // Get all document ids that don't match the select all checkbox
    const emailIds = emails ? emails.filter(x => !!x.selected !== allSelected).map(x => x.id) : [];
    return {
      emailIds,
      selectAll: allSelected,
    };
  };

  executeBatchAction = ({ action, parameters }) => {
    if (this.isGlobalActionsDisabled()) return Promise.reject(new Error('invalid'));

    const { intl } = this.props;
    const { currentSearchParams } = this.state;
    const { stateService } = this.props;

    if (action === 'downloadzip') {
      const connectionId = SignalRService.getConnectionId();
      parameters = {
        ...parameters,
        signalRConnectionId: connectionId,
      };
    }

    const jobParameters = { ...parameters, ...this.getSelectionParams() };

    const url = `${config.appUrls.documents}/api/emails/${action}`;
    const allParams = {
      filterParams: currentSearchParams,
      ...jobParameters,
    };

    return api.base
      .post(url, allParams)
      .then(({ data }) => {
        this.setState(state => ({
          ...state,
          showUpdateTagsModal: false,
          showUpdateRelationsModal: false,
          showRemoveBatchDocumentsModal: false,
        }));

        const onClick = data ? () => stateService.gotoJob(data.id) : undefined;

        ToastrService.info(intl.formatMessage(messages.GlobalActionStarted), undefined, onClick);
      })
      .catch(handleHttpError(intl));
  };
  resendEmail = docId => {
    if (!docId) return;
    const { intl } = this.props;

    return api.base.post(`${config.appUrls.documents}/api/emails/Resend/${docId}`).then(
      ({ data }) => {
        if (!data) return;
        this.setState({ showResendConfirmation: false });
        ToastrService.success(intl.formatMessage(messages.TheMailHasBeenSentAgain));
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  render() {
    const {
      initialSearchParams,
      emails,
      activeEmail,
      showDocumentDetailsModal,
      allSelected,
      showUpdateTagsModal,
      showUpdateRelationsModal,
      showRemoveBatchDocumentsModal,
      showSingleDeleteDocumentModal,
      showForwardModal,
      showCreateTodoModal,
      showSetPublicOnPortalModal,
      showShareItemModal,
      showDeleteRelationLinkModal,
      showChangeDocumentDateModal,
      activeRelationId,
      showResendConfirmation,
      showReplyToModal,
      replyAll,
    } = this.state;
    const documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=-1`; // -1 = document type unknown, used for recognizing
    const users = DataCacheService.getUsers();

    const { intl } = this.props;
    return (
      <div className="emails-container">
        {showResendConfirmation && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.ResendEmail)}
            message={intl.formatMessage(messages.ResendEmailConfirmationMsg)}
            onNo={() => this.setState({ showResendConfirmation: false })}
            onSave={() => this.resendEmail(activeEmail.documentId)}
          />
        )}
        {showForwardModal && (
          <ForwardEmailModal
            intl={intl}
            documentId={activeEmail.documentId}
            relationId={
              activeEmail.relations && activeEmail.relations.length === 1
                ? activeEmail.relations[0].id
                : null
            }
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showForwardModal: false,
              }))
            }
          />
        )}
        {showReplyToModal && (
          <ReplyToEmailModal
            intl={intl}
            documentId={activeEmail.documentId}
            replyAll={replyAll}
            show
            onClose={() => this.setState({ showReplyToModal: false })}
          />
        )}
        {showDocumentDetailsModal && (
          <DocumentDetailsModal
            show={showDocumentDetailsModal}
            documentId={activeEmail.documentId}
            getPreviousDocumentId={this.getPreviousDocumentId}
            getNextDocumentId={this.getNextDocumentId}
            onRemoveDocument={document =>
              this.setState({
                activeEmail: { documentId: document.id },
                showSingleDeleteDocumentModal: true,
              })
            }
            onSave={document =>
              this.setState({ showDocumentDetailsModal: false }, () =>
                this.onDetailModalClosed(document),
              )
            }
            onClose={() =>
              this.setState({ showDocumentDetailsModal: false, activeEmail: undefined })
            }
            onShareItem={() => this.setState({ showShareItemModal: true })}
          />
        )}

        {showUpdateTagsModal && (
          <UpdateDocumentTagsModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateTagsModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'updatetags',
                parameters: data,
              })
            }
          />
        )}
        {showUpdateRelationsModal && (
          <UpdateDocumentRelationsModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showUpdateRelationsModal: false,
              }))
            }
            onSave={data => {
              const parameters = {
                action: data.action,
                relations: data.relations.map(x => x.id),
              };
              return this.executeBatchAction({
                action: 'updaterelations',
                parameters,
              });
            }}
          />
        )}
        {showDeleteRelationLinkModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteRelationLink)}
            message={intl.formatMessage(messages.DeleteRelationLinkConfirmationMsg)}
            onNo={() => this.setState({ showDeleteRelationLinkModal: false })}
            onSave={() => {
              api.base
                .delete(
                  `${config.appUrls.documents}/api/documents/Links/${activeEmail.documentId}/${activeRelationId}/3`,
                )
                .then(
                  () => {
                    ToastrService.success(intl.formatMessage(messages.RelationLinkDeleted));
                    this.setState({
                      showDeleteRelationLinkModal: false,
                      emails: emails.map(x => {
                        if (x.documentId === activeEmail.documentId) {
                          return {
                            ...x,
                            relations: x.relations.filter(y => y.id !== activeRelationId),
                          };
                        }
                        return x;
                      }),
                    });
                  },
                  error => {
                    handleHttpError(intl)(error);
                  },
                );
            }}
          />
        )}
        {showRemoveBatchDocumentsModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteDocument)}
            message={intl.formatMessage(messages.DeleteMultipleDocumentsConfirmationMsg)}
            onNo={() => this.setState({ showRemoveBatchDocumentsModal: false })}
            onSave={() =>
              this.executeBatchAction({
                action: 'removedocuments',
                parameters: {},
              })
            }
          />
        )}
        {showSingleDeleteDocumentModal && (
          <YesNoModal
            show
            title={intl.formatMessage(messages.DeleteDocument)}
            message={intl.formatMessage(messages.DeleteSingleDocumentConfirmationMsg)}
            onNo={() => this.setState({ showSingleDeleteDocumentModal: false })}
            onSave={() => this.deleteDocument(activeEmail.documentId)}
          />
        )}

        {showSetPublicOnPortalModal && (
          <SetIsPublicOnPortalModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showSetPublicOnPortalModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'setispubliconportal',
                parameters: data,
              })
            }
          />
        )}
        {showChangeDocumentDateModal && (
          <ChangeDocumentDateModal
            intl={intl}
            show
            onClose={() =>
              this.setState(state => ({
                ...state,
                showChangeDocumentDateModal: false,
              }))
            }
            onSave={data =>
              this.executeBatchAction({
                action: 'ChangeDocumentDate',
                parameters: data,
              })
            }
          />
        )}
        {showCreateTodoModal && (
          <CreateToDoModal
            show
            documents={[
              {
                id: activeEmail.documentId,
                name: activeEmail.filename,
              },
            ]}
            relationId={
              activeEmail.relations && activeEmail.relations.length > 0
                ? activeEmail.relations[0].id
                : undefined
            }
            onSave={() => this.setState({ showCreateTodoModal: false, activeEmail: undefined })}
            onClose={() => this.setState({ showCreateTodoModal: false, activeEmail: undefined })}
          />
        )}

        {showShareItemModal && (
          <ShareItemModal
            show
            type="document"
            id={activeEmail.documentId}
            itemName={activeEmail.filename}
            relationId={
              activeEmail.relations && activeEmail.relations.length > 0
                ? activeEmail.relations[0].id
                : undefined
            }
            relationName={
              activeEmail.relations && activeEmail.relations.length > 0
                ? getRelationName(intl, activeEmail.relations[0])
                : undefined
            }
            onClose={() => this.setState({ showShareItemModal: false })}
          />
        )}
        <div style={{ display: 'none' }}>
          <CanCreate on="documents">
            <Dropzone
              documentUploadEndpoint={documentUploadEndpoint}
              onDocumentUploaded={this.onDocumentUploaded}
              pondRef={ref => {
                this.pond = ref;
              }}
            />
          </CanCreate>
        </div>
        <div className="emails-table-container col-md-12">
          <SimplePanel title={<span>{intl.formatMessage(messages.Emails)}</span>}>
            <EmailFilters
              filters={initialSearchParams.mainFilters}
              onFilterChange={filters => this.filtersChanged(filters)}
              selectionCount={this.getSelectionCountFormatted()}
              users={users}
            />
          </SimplePanel>
          <div className="row btn-row">
            <div className="col-md-6 pull-right content-right">
              <EmailGlobalActionsButton
                disabled={this.isGlobalActionsDisabled()}
                onUpdateTags={() => this.setState({ showUpdateTagsModal: true })}
                onUpdateRelations={() => this.setState({ showUpdateRelationsModal: true })}
                onRemoveDocuments={() => this.setState({ showRemoveBatchDocumentsModal: true })}
                onDownloadZip={() =>
                  this.executeBatchAction({
                    action: 'downloadzip',
                    parameters: {
                      useFolderStructure: false,
                    },
                  })
                }
                onSendByEmail={() => this.checkCanSendEmail()}
              />
            </div>
          </div>

          <div
            className="dropzone dropable-files"
            dropinfo-files={intl.formatMessage(messages.UploadFilesToStartRecognition)}
            onDragOver={e => this.handleListDragOver(e)}
            onDrop={e => this.handleListDrop(e)}
            style={{
              minHeight: '50vh',
            }}
          >
            <ScrollableTable
              tableClassName="table-grid margin50B"
              initialSearchParams={initialSearchParams}
              items={emails}
              noDataLabel={intl.formatMessage(messages.NoEmailsFound)}
              loadItems={(searchParams, requestNr) => this.loadEmails(searchParams, requestNr)}
              renderRow={item => this.renderRow(item)}
            >
              <ColumnHeader
                title={
                  <Checkbox
                    extraClass="margin2L"
                    onClick={this.handleSelectAll}
                    value={allSelected}
                  />
                }
                className=""
              />

              <ColumnHeader
                title={intl.formatMessage(messages.Subject)}
                className="col-md-5 col-sm-3"
                sortKey="subject"
              />
              <ColumnHeader title={intl.formatMessage(messages.Relation)} className="col-md-1" />
              <ColumnHeader
                title={intl.formatMessage(messages.From)}
                sortKey="sender"
                className="col-md-2 col-sm-1"
              />
              <ColumnHeader
                title={intl.formatMessage(messages.EmailTo)}
                sortKey="recipients"
                className="col-md-2 col-sm-1"
              />
              <ColumnHeader
                title={intl.formatMessage(messages.DocumentDate)}
                sortKey="documentDate"
                style={{ width: '6%' }}
              />
              <ColumnHeader
                title={intl.formatMessage(messages.CreatedOn)}
                sortKey="creationDate"
                style={{ width: '6%' }}
              />

              <ColumnHeader title="" className="col-md-2 col-sm-1" />
            </ScrollableTable>
          </div>
        </div>
      </div>
    );
  }
}

export default showWhenPermission(
  'Documents',
  'read',
)(injectStateService(injectIntl(withDropZone(EmailsContainer))));
